import React from 'react';
import type { Edge, OnSelectionChangeParams, Node } from 'reactflow';
import wbsSettings from './WbsGraphSettings';

const { NODE_HEIGHT, LEAF_SPACING, BRANCH_SPACING } = wbsSettings;

interface OnNewNodeAddParams {
	parentId: string | 'root';
	parentTitle: string;
	type?: 'branchNode' | 'leafNode';
	data?: WbsBaseNode;
	position?: { x: number; y: number };
}

interface WbsBaseNode {
	parentId?: string | 'root';
	parentTitle?: string;
	selected?: boolean;
	isAdd?: boolean;
	isEditable?: boolean;
	onNewNodeAdd: (params: OnNewNodeAddParams) => void;
	onBranchExpand: (branchId: string, expanded: boolean) => void;

	id?: string;
	module?: string;
	node: any;
}

export interface WbsRootNode extends WbsBaseNode {
	table: string;
	tableId: string;
	title: string;
	dateFrom: string;
	dateTo: string;
	hasWbs: boolean;
	position: { x: number; y: number };
}

export interface WbsNode extends WbsBaseNode {
	expanded?: boolean;
}

interface WbseStateProps {
	rootNode: WbsRootNode;
	setNodes: React.Dispatch<React.SetStateAction<Node<any, string | undefined>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge<any>[]>>;
	setSelectedNode: React.Dispatch<React.SetStateAction<any | undefined>>;
	updateNodeViewerRefresh: VoidFunction;
}

export default function useWbsState({
	rootNode,
	setNodes,
	setEdges,
	setSelectedNode,
	updateNodeViewerRefresh
}: WbseStateProps) {
	const [expandedNodes, setExpandedNodes] = React.useState<any[]>([]);

	const addWbsNode = React.useCallback(
		(newNode: Node<WbsNode | WbsRootNode>) => {
			const { data } = newNode;
			const newEdge = {
				id: `${data.parentId ? `${data.parentId}+` : ''}${newNode.id}`,
				source: data.parentId && `${data.parentId}`,
				target: newNode.id,
				// hidden: newNode.hidden,
				type: 'smoothstep'
				// animated: true
			} as Edge;

			setNodes(state => [...state, newNode]);
			setEdges(state => [...state, newEdge]);
		},
		[setEdges, setNodes]
	);

	const editWbsNode = React.useCallback(
		(editedNote: Node<WbsNode>) => {
			setNodes(nodes => {
				const nodeIndex = nodes.findIndex(node => node.id === editedNote.id);
				nodes[nodeIndex] = editedNote;
				return nodes;
			});
		},
		[setNodes]
	);

	const onBranchExpand = React.useCallback((branchId: string, expanded: boolean) => {
		setExpandedNodes(nodes => {
			if (!expanded) return nodes.filter(node => node !== branchId);
			nodes.push(branchId);
			return [...nodes];
		});
	}, []);

	const setHighlightedWbsNode = React.useCallback(
		(nodeId: string) => {
			setNodes(nodes => nodes.map(node => ({ ...node, data: { ...node.data, selected: nodeId === node.id } })));
		},
		[setNodes]
	);

	const onSelectionChange = React.useCallback(
		(selection: OnSelectionChangeParams) => {
			const selectedNode = selection.nodes[0];

			if (selectedNode && selectedNode?.id !== 'root') {
				setHighlightedWbsNode(selectedNode?.id);
				setSelectedNode(selectedNode);
				updateNodeViewerRefresh();
			}
		},
		[setHighlightedWbsNode, setSelectedNode, updateNodeViewerRefresh]
	);

	const onNewNodeAdd = React.useCallback(
		({ parentId, parentTitle }: OnNewNodeAddParams) => {
			onBranchExpand(parentId, true);
			setHighlightedWbsNode(parentId);
			setSelectedNode({
				data: { parentId: parentId !== 'root' ? parentId : undefined, parentTitle, isAdd: true }
			});
			updateNodeViewerRefresh();
		},
		[onBranchExpand, setHighlightedWbsNode, setSelectedNode, updateNodeViewerRefresh]
	);

	const mapWbsNodes = React.useCallback(
		({ nodes, parentId, offset }: { nodes: any[]; parentId: string; offset: { x: number; y: number } }) => {
			if (parentId === 'root') {
				addWbsNode({
					id: 'root',
					type: 'rootNode',
					data: { ...rootNode, onNewNodeAdd },
					position: { x: rootNode.position.x, y: rootNode.position.y }
				});
			}

			nodes.forEach(node => {
				const expanded = expandedNodes.find(collapsedNode => collapsedNode === node.wbsn_uid);

				offset.y += NODE_HEIGHT + LEAF_SPACING;

				addWbsNode({
					id: node.wbsn_uid,
					type: !node.wbsn_is_leaf ? 'branchNode' : 'leafNode',
					data: {
						node,
						parentId: parentId || 'root',
						selected: false,
						expanded,
						isEditable: !node.wbsn_converted,
						onBranchExpand,
						onNewNodeAdd
					},
					position: { x: offset.x + BRANCH_SPACING, y: offset.y }
				});

				if (expanded && node.wbsn_children) {
					const childrensOffset = mapWbsNodes({
						nodes: node.wbsn_children,
						parentId: node.wbsn_uid,
						offset: { x: offset.x + BRANCH_SPACING, y: offset.y }
					});

					offset.y = childrensOffset.y;
				}
			});

			return offset;
		},
		[addWbsNode, expandedNodes, onBranchExpand, onNewNodeAdd, rootNode]
	);

	return { mapWbsNodes, onSelectionChange };
}
